import React, { useState, useMemo, useContext, useEffect } from 'react';
import _ from 'lodash';
import classNames from 'classnames';

import { FormControls } from '@components/forms/formControls';
import { IFilterOption, IFilterResult, getFilterOptionIdString } from '@components/filters/filterLib';
import { useTranslation } from '@lib/useTypedTranslation';

import { FilterFormProps } from './popoverDialog';
import { FilterContext } from './context';

import './listFilter.css';

interface ListFilterProps extends FilterFormProps {
  filterField: keyof IFilterResult,
	maxSelect?: number,
  showNot?: boolean
}

export const ListFilter: React.FC<ListFilterProps> = ({ close, filterField, maxSelect = 20, showNot = false }) => {
  const { handleCancel, handleFiltersUpdated, filterOptions, filters } = useContext(FilterContext);
  const { t } = useTranslation(['translation', 'filters']);
  const filterRef = React.useRef<HTMLDivElement>(null);

  // Stop native focusin from bubbling to the handy-scroll container.
  // React's onFocus is delegated at the document root, so by the time it fires
  // handy-scroll's listener has already queued a setTimeout that can crash
  // if the DataTable remounts before the timer fires.
  React.useEffect(() => {
    const el = filterRef.current!;
    const stopFocus = (e: Event) => e.stopPropagation();
    el.addEventListener('focusin', stopFocus, false);
    return () => el.removeEventListener('focusin', stopFocus, false);
  }, []);

  const negateKey = `${filterField}_negate`;

  const initialNegate = useMemo(() => filters[negateKey] === true, [filters, negateKey]);
  const initialValue = useMemo(() => (filters[filterField] as IFilterOption[] | undefined) || [], [filters, filterField]);
  const [selected, setSelected] = useState<IFilterOption[]>(initialValue);
  const [negate, setNegate] = useState<boolean>(initialNegate);
  const [search, setSearch] = useState('');

  const hasChanged = !_.isEqual(initialValue, selected) || negate !== initialNegate;

  const searchedOptions = useMemo(() => {
    const options = [...((filterOptions.availableFilters[filterField] as IFilterOption[] | undefined) ?? [])]
      .sort((a, b) => {
        const bSelected = initialValue.some(item => getFilterOptionIdString(item) === getFilterOptionIdString(b));
        const aSelected = initialValue.some(item => getFilterOptionIdString(item) === getFilterOptionIdString(a));

        return (bSelected ? 1 : 0) - (aSelected ? 1 : 0);
      })
      .map(g => {
        if (typeof g === 'string') {
          return { label: g, value: g };
        }
        return { label: g.value || g.id, value: g.id, nullFilter: g.nullFilter };
      });

    if (!search) return options;

    const s = search.toLowerCase();
    return options.filter(opt => opt.label.toLowerCase().includes(s));
  }, [search, filterOptions, filterField, initialValue]);

  const handleSelect = (optionValue: string) => {
    const isSelected = selected.some(item => getFilterOptionIdString(item) === optionValue);

    let next;
    if (isSelected) {
      next = selected.filter(item => getFilterOptionIdString(item) !== optionValue);
    } else {
      // Both the array and option must exist since optionValue comes from a rendered option
      const option = (filterOptions.availableFilters[filterField] as IFilterOption[])!.filter(opt =>
        getFilterOptionIdString(opt) === optionValue
      )!;

      next = [...selected, ...option];
    }
    setSelected(next);
  };

  return (
    <div className="list-filter" ref={filterRef}>
      <div className="filter-dialog_popover-content">
        {showNot && (
          <label className="list-filter_not">
            <input
              type="checkbox"
              checked={negate}
              onChange={() => setNegate(!negate)}
              data-id={`${negateKey}-checkbox`}
            />
            {t('NOT', { ns: 'filters' })}
          </label>
        )}
        <input
          type="text"
          placeholder={t('SEARCH', { ns: 'translation' })}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="list-filter_search"
          autoFocus
        />
        <div className="list-filter_options">
          {searchedOptions?.map(({ label, value, nullFilter }) => {
            const optValue = getFilterOptionIdString({ id: value, value: label, nullFilter });
            const isSelected = selected.some(item => getFilterOptionIdString(item) === optValue);
            const disabled = !isSelected && selected.length >= maxSelect;

            return <label key={optValue} className={classNames("list-filter_option", { 'null-filter': nullFilter, disabled })} data-id={nullFilter ? 'null-filter-option' : undefined}>
              <input
                type="checkbox"
                checked={isSelected}
                disabled={disabled}
                onChange={() => handleSelect(optValue)}
                data-id={`checkbox-id--${optValue}`}
                data-cy={`checkbox-id--${optValue}`}
              />
              {label}
            </label>;
          })}
        </div>
      </div>
      <FormControls
        hideStatus
        mode="apply"
        submitDisabled={!hasChanged}
        onCancel={() => {
          close();

          handleCancel?.();
        }}
        onSubmit={() => {
          close();

          const newValue: IFilterResult = { ...filters };
          newValue[filterField] = selected;
          if (negate) {
            newValue[negateKey] = true;
          } else {
            delete newValue[negateKey];
          }
          handleFiltersUpdated(newValue);
        }}
      />
    </div>
  );
};
